import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const WRAPPER_STYLES =
  'position:fixed;top:-99999px;left:-99999px;width:760px;' +
  'color-scheme:light;' +
  '--background:0 0% 100%;--foreground:0 0% 5%;' +
  '--card:0 0% 100%;--card-foreground:0 0% 5%;' +
  '--popover:0 0% 100%;--popover-foreground:0 0% 5%;' +
  '--primary:166 30% 62%;--primary-foreground:0 0% 100%;' +
  '--secondary:210 40% 96.1%;--secondary-foreground:222.2 47.4% 11.2%;' +
  '--muted:0 0% 90%;--muted-foreground:0 0% 36%;' +
  '--accent:0 0% 95%;--accent-foreground:222.2 47.4% 11.2%;' +
  '--destructive:0 84.2% 60.2%;--destructive-foreground:210 40% 98%;' +
  '--border:0 0% 90%;--input:0 0% 90%;--ring:340 92% 52%;' +
  'background:#fff;color:#111;padding:40px;' +
  'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;' +
  'font-size:14px;line-height:1.7;';

const TYPOGRAPHY_CSS = `
  * { box-sizing: border-box; }
  h1,h2,h3,h4,h5,h6 { margin:.9em 0 .3em; font-weight:600; line-height:1.3; }
  h1{font-size:1.5em}h2{font-size:1.25em}h3{font-size:1.1em}
  p { margin:.45em 0; }
  ul,ol { margin:.45em 0; padding-left:1.5em; }
  li { margin:.2em 0; }
  code { background:#f3f4f6; border:1px solid #e5e7eb; border-radius:3px;
         padding:.12em .3em; font-family:monospace; font-size:.9em; }
  pre  { background:#f3f4f6; border:1px solid #e5e7eb; border-radius:6px;
         padding:.9em; overflow-x:auto; margin:.65em 0; }
  pre code { background:none; border:none; padding:0; }
  blockquote { border-left:3px solid #d1d5db; margin:.65em 0;
               padding:.2em 0 .2em .9em; color:#6b7280; }
  table { border-collapse:collapse; width:100%; margin:.65em 0; }
  th,td { border:1px solid #d1d5db; padding:.45em .65em; text-align:left; }
  th { background:#f9fafb; font-weight:600; }
  a  { color:hsl(var(--primary)); text-decoration:underline; text-underline-offset:2px; }
  hr { border:none; border-top:1px solid #e5e7eb; margin:.9em 0; }
`;

/**
 * Generates a jsPDF document from a rendered message element.
 * Caller is responsible for what to do with the returned doc (save or output as blob).
 */
export async function generateMessagePdf(messageId: string): Promise<jsPDF> {
  const step = document.getElementById(`step-${messageId}`);
  const contentEl = step?.querySelector('.message-content') as HTMLElement | null;
  if (!contentEl) throw new Error('Message content not found');

  const wrapper = document.createElement('div');
  wrapper.style.cssText = WRAPPER_STYLES;

  const style = document.createElement('style');
  style.textContent = TYPOGRAPHY_CSS;
  wrapper.appendChild(style);

  const content = document.createElement('div');
  content.innerHTML = contentEl.innerHTML;
  wrapper.appendChild(content);

  const disclaimer = document.createElement('p');
  disclaimer.textContent = 'AI-generated content - please review before using.';
  disclaimer.style.cssText =
    'margin-top:32px;padding-top:12px;border-top:1px solid #e5e7eb;' +
    'font-size:11px;color:#9ca3af;font-style:italic;text-align:center;';
  wrapper.appendChild(disclaimer);

  document.body.appendChild(wrapper);

  try {
    const linkData: { href: string; x: number; y: number; w: number; h: number }[] = [];
    const wrapperRect = wrapper.getBoundingClientRect();

    content.querySelectorAll('a[href]').forEach((a) => {
      const anchor = a as HTMLAnchorElement;
      const href = anchor.href;
      if (!href || href.startsWith('javascript:') || href.startsWith('#')) return;
      const r = anchor.getBoundingClientRect();
      linkData.push({
        href,
        x: r.left - wrapperRect.left,
        y: r.top - wrapperRect.top,
        w: r.width,
        h: r.height,
      });
    });

    const canvas = await html2canvas(wrapper, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    const PAGE_W = 595.28;
    const PAGE_H = 841.89;
    const MARGIN = 40;
    const contentWidthPt = PAGE_W - MARGIN * 2;
    const pageContentH = PAGE_H - MARGIN * 2;

    const pxToPt = contentWidthPt / wrapper.offsetWidth;
    const canvasRatioY = canvas.height / wrapper.offsetHeight;

    const totalHeightPt = wrapper.offsetHeight * pxToPt;
    const totalPages = Math.ceil(totalHeightPt / pageContentH);

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });

    for (let i = 0; i < totalPages; i++) {
      if (i > 0) doc.addPage();

      const srcYPx = (i * pageContentH / pxToPt) * canvasRatioY;
      const srcHPx = Math.min(
        (pageContentH / pxToPt) * canvasRatioY,
        canvas.height - srcYPx
      );

      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = Math.ceil(srcHPx);
      const ctx = pageCanvas.getContext('2d')!;
      ctx.drawImage(canvas, 0, srcYPx, canvas.width, srcHPx, 0, 0, canvas.width, srcHPx);

      const imgH = (srcHPx / canvasRatioY) * pxToPt;
      doc.addImage(pageCanvas.toDataURL('image/png'), 'PNG', MARGIN, MARGIN, contentWidthPt, imgH);
    }

    linkData.forEach(({ href, x, y, w, h }) => {
      const linkYPt = y * pxToPt;
      const page = Math.floor(linkYPt / pageContentH);
      const localY = linkYPt - page * pageContentH;
      if (page < totalPages) {
        doc.setPage(page + 1);
        doc.link(MARGIN + x * pxToPt, MARGIN + localY, w * pxToPt, h * pxToPt, { url: href });
      }
    });

    return doc;
  } finally {
    document.body.removeChild(wrapper);
  }
}
